export interface MeUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "director" | "manager" | "staff";
  isAdmin: boolean;
  isActive: boolean;
  mustChangePassword: boolean;
}

export interface Me {
  user: MeUser;
  permittedSlugs: string[];
}

export async function fetchMe(): Promise<Me | null> {
  const res = await fetch("/auth/me", { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`fetchMe: ${res.status}`);
  return (await res.json()) as Me;
}

export function signInRedirect(): void {
  // Local-login first; the page also offers a Google button as a fallback
  // until Phase 4 removes it.
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true; user: MeUser } | { ok: false; error: string }> {
  const res = await fetch("/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (res.ok) {
    const data = (await res.json()) as { user: MeUser };
    return { ok: true, user: data.user };
  }
  const data = await res.json().catch(() => ({ error: "Login failed." }));
  return { ok: false, error: data.error ?? "Login failed." };
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch("/auth/change-password", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (res.ok) return { ok: true };
  const data = await res.json().catch(() => ({ error: "Change failed." }));
  return { ok: false, error: data.error ?? "Change failed." };
}

export async function signOut(): Promise<void> {
  await fetch("/auth/logout", { method: "POST", credentials: "include" });
  window.location.href = "/login";
}
